import { useState, useCallback } from "react";
import { FileUp, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";

interface CVUploadZoneProps {
  onFileSelect: (file: File) => void;
  isLoading: boolean;
  progress?: number;
  progressStatus?: string;
}

export const CVUploadZone = ({ 
  onFileSelect, 
  isLoading, 
  progress = 0, 
  progressStatus = "" 
}: CVUploadZoneProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const { t } = useTranslation();
  
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);
  
  const handleDragLeave = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      
      // Check file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: t("candidates.cv.invalidFileTypeTitle"),
          description: t("candidates.cv.invalidFileTypeDesc"),
          variant: "destructive",
        });
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: t("candidates.cv.fileTooLargeTitle"),
          description: t("candidates.cv.fileTooLargeDesc"),
          variant: "destructive",
        });
        return;
      }
      
      onFileSelect(file);
    }
  }, [onFileSelect, t]);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      
      // Check file type
      const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast({
          title: t("candidates.cv.invalidFileTypeTitle"),
          description: t("candidates.cv.invalidFileTypeDesc"),
          variant: "destructive",
        });
        return;
      }
      
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: t("candidates.cv.fileTooLargeTitle"),
          description: t("candidates.cv.fileTooLargeDesc"),
          variant: "destructive",
        });
        return;
      }
      
      onFileSelect(file);
      e.target.value = ''; // Reset file input
    }
  };
  
  return (
    <div 
      className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors h-full min-h-[300px] flex flex-col items-center justify-center ${
        isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isLoading ? (
        <div className="w-full max-w-md">
          <h3 className="text-lg font-medium mb-4">{t("candidates.cv.processingCV")}</h3>
          <Progress value={progress} className="h-2 mb-2" />
          <p className="text-sm text-gray-500">{progressStatus || t("candidates.cv.pleaseWait")}</p>
        </div>
      ) : (
        <div className="flex flex-col items-center">
          <Upload className="h-16 w-16 text-gray-400 mb-6" />
          <p className="text-lg font-medium mb-3">{t("candidates.cv.dragAndDrop")}</p>
          <p className="text-sm text-gray-500 mb-6">{t("candidates.cv.or")}</p>
          
          <Button 
            variant="outline" 
            size="lg" 
            className="flex items-center gap-2 mb-6"
            disabled={isLoading}
            onClick={() => document.getElementById('cv-upload')?.click()}
            type="button"
          >
            <FileUp className="h-5 w-5" />
            <span>{t("candidates.cv.browseFiles")}</span>
          </Button>
          <input 
            id="cv-upload"
            type="file"
            accept=".pdf,.doc,.docx,.txt"
            onChange={handleFileChange}
            className="hidden"
            disabled={isLoading}
          />
          
          <p className="text-sm text-gray-500">
            {t("candidates.cv.supportedFormats")}
          </p>
        </div>
      )}
    </div>
  );
};
